import { useState, useEffect } from 'react';
import { supabase } from '@/lib/supabase/client';
import { useBusinessConfig } from '@/hooks/useBusinessConfig';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { toast } from 'sonner';
import { FileText, Send, CheckCircle, Clock, AlertCircle, Plus, Eye, Trash2 } from 'lucide-react';
import { formatAUDate, toDateInputValue } from '@/lib/utils/dateFormat';
import { deductInventoryFromInvoice, reserveStockForInvoice } from '@/lib/vbs/utils/inventory-logic';

interface Invoice {
  id: string;
  invoice_number: string;
  quote_id?: string;
  customer_name: string;
  customer_email: string;
  customer_phone?: string;
  customer_address?: string;
  subtotal: number;
  gst_amount: number;
  total_amount: number;
  amount_paid: number;
  amount_due: number;
  status: 'unpaid' | 'paid' | 'overdue' | 'partially_paid';
  due_date: string;
  paid_at?: string;
  notes?: string;
  email_sent: boolean;
  created_at: string;
}

interface Quote {
  id: string;
  customer_name: string;
  customer_email: string;
  customer_phone?: string;
  customer_address?: string;
  total_amount: number;
  notes?: string;
}

export function InvoiceManager() {
  const { config } = useBusinessConfig();
  const [invoices, setInvoices] = useState<Invoice[]>([]);
  const [loading, setLoading] = useState(true);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [quotes, setQuotes] = useState<Quote[]>([]);

  useEffect(() => {
    fetchInvoices();
  }, []);

  const fetchInvoices = async () => {
    setLoading(true);
    try {
      const { data, error } = await supabase
        .from('invoices')
        .select('*')
        .order('created_at', { ascending: false });

      if (error) {
        console.error('Invoice fetch error:', error);
        throw error;
      }
      setInvoices(data || []);
    } catch (error) {
      console.error('Error fetching invoices:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchUnpaidQuotes = async () => {
    try {
      const { data, error } = await supabase
        .from('quotes')
        .select('*')
        .eq('approved', true)
        .is('invoice_id', null)
        .order('created_at', { ascending: false });

      if (error) throw error;
      setQuotes(data || []);
    } catch (error) {
      console.error('Error fetching quotes:', error);
    }
  };

  const handleSendInvoice = async (invoice: Invoice) => {
    try {
      const { data: items } = await supabase
        .from('invoice_items')
        .select('*')
        .eq('invoice_id', invoice.id);

      const response = await fetch('/api/send-invoice', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          invoice: {
            ...invoice,
            line_items: items || [],
          },
          config,
        }),
      });

      const result = await response.json();

      if (result.success) {
        toast.success('Invoice sent successfully!');
        await supabase
          .from('invoices')
          .update({ email_sent: true, emailed_at: new Date().toISOString() })
          .eq('id', invoice.id);
        fetchInvoices();
      } else {
        throw new Error('Failed to send');
      }
    } catch (error) {
      console.error('Error sending invoice:', error);
      toast.error('Failed to send invoice');
    }
  };

  const handleMarkAsPaid = async (invoiceId: string) => {
    try {
      const invoice = invoices.find(i => i.id === invoiceId);
      if (!invoice) return;

      const { error } = await supabase
        .from('invoices')
        .update({
          status: 'paid',
          amount_paid: invoice.total_amount,
          amount_due: 0,
          paid_at: new Date().toISOString(),
        })
        .eq('id', invoiceId);

      if (error) throw error;

      // Auto-deduct inventory if invoice has items (finalizes reserved stock)
      if (config?.contact_phone) {
        const alerts = await deductInventoryFromInvoice(invoiceId, config.contact_phone);
        if (alerts.length > 0) {
          toast.warning('Low stock alert! Some items need reordering.');
        }
      }

      toast.success('Invoice marked as paid!');
      fetchInvoices();
    } catch (error) {
      console.error('Error marking invoice as paid:', error);
      toast.error('Failed to update invoice');
    }
  };

  const getStatusIcon = (status: string) => {
    switch (status) {
      case 'paid':
        return <CheckCircle className="w-4 h-4 text-green-600" />;
      case 'overdue':
        return <AlertCircle className="w-4 h-4 text-red-600" />;
      case 'partially_paid':
        return <Clock className="w-4 h-4 text-amber-600" />;
      default:
        return <Clock className="w-4 h-4 text-slate-600" />;
    }
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'paid':
        return 'bg-green-50 text-green-700 border-green-200';
      case 'overdue':
        return 'bg-red-50 text-red-700 border-red-200';
      case 'partially_paid':
        return 'bg-amber-50 text-amber-700 border-amber-200';
      default:
        return 'bg-slate-50 text-slate-700 border-slate-200';
    }
  };

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <FileText className="w-5 h-5 text-purple-600" />
          <h3 className="font-black text-slate-900 uppercase">Invoices</h3>
        </div>
        <Button
          onClick={() => {
            fetchUnpaidQuotes();
            setShowCreateModal(true);
          }}
          className="flex items-center gap-2"
          size="sm"
        >
          <Plus className="w-4 h-4" />
          Create Invoice
        </Button>
      </div>

      {loading ? (
        <div className="text-center py-12">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-purple-600 mx-auto"></div>
          <p className="mt-4 text-slate-600">Loading invoices...</p>
        </div>
      ) : invoices.length === 0 ? (
        <div className="text-center py-12 text-slate-500">
          <FileText className="w-12 h-12 mx-auto mb-3 opacity-20" />
          <p className="font-bold text-sm">No invoices yet</p>
          <p className="text-xs mt-1">Create an invoice from an approved quote</p>
        </div>
      ) : (
        <div className="grid gap-3">
          {invoices.map((invoice) => (
            <div
              key={invoice.id}
              className="bg-white border-2 border-slate-100 rounded-[2rem] p-6 shadow-sm hover:border-purple-200 transition-colors"
            >
              <div className="flex justify-between items-start mb-4">
                <div className="flex items-center gap-3">
                  {getStatusIcon(invoice.status)}
                  <div>
                    <span className={`text-[8px] font-black bg-purple-50 text-purple-600 px-2 py-0.5 rounded uppercase ${getStatusColor(invoice.status)}`}>
                      {invoice.status.replace('_', ' ')}
                    </span>
                    <h4 className="text-lg font-black uppercase text-slate-900 mt-1 leading-none">
                      {invoice.customer_name}
                    </h4>
                    <p className="text-[9px] font-bold text-slate-400 uppercase mt-1">
                      #{invoice.invoice_number} · Created: {formatAUDate(invoice.created_at)}
                    </p>
                  </div>
                </div>
                <div className="text-right">
                  <p className="text-xl font-black italic text-slate-900">
                    ${invoice.total_amount.toLocaleString()}
                  </p>
                  {invoice.amount_due > 0 && (
                    <p className="text-[8px] font-black text-red-600 uppercase">
                      Due: ${invoice.amount_due.toLocaleString()}
                    </p>
                  )}
                  <p className="text-[8px] font-bold text-slate-400 uppercase mt-1">
                    Due: {formatAUDate(invoice.due_date)}
                  </p>
                </div>
              </div>

              <div className="flex gap-2">
                {!invoice.email_sent && (
                  <Button
                    variant="outline"
                    className="flex-1 rounded-xl border-slate-100 font-black uppercase text-[10px] gap-2 h-10"
                    onClick={() => handleSendInvoice(invoice)}
                  >
                    <Send className="w-3 h-3" />
                    Send Invoice
                  </Button>
                )}
                {invoice.status !== 'paid' && (
                  <Button
                    variant="outline"
                    className="flex-1 rounded-xl border-green-200 text-green-700 font-black uppercase text-[10px] gap-2 h-10 hover:bg-green-50"
                    onClick={() => handleMarkAsPaid(invoice.id)}
                  >
                    <CheckCircle className="w-3 h-3" />
                    Mark Paid
                  </Button>
                )}
                <Button
                  variant="ghost"
                  size="sm"
                  className="rounded-xl font-black uppercase text-[10px] gap-2 h-10"
                  onClick={async () => {
                    const { data: items } = await supabase
                      .from('invoice_items')
                      .select('*')
                      .eq('invoice_id', invoice.id);
                    toast.info(`Invoice has ${items?.length || 0} line items`);
                  }}
                >
                  <Eye className="w-3 h-3" />
                  View Items
                </Button>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Create Invoice Modal */}
      {showCreateModal && (
        <CreateInvoiceModal
          quotes={quotes}
          config={config || { biz_name: 'Your Business', biz_type: 'business', gst_registered: true }}
          onClose={() => setShowCreateModal(false)}
          onSuccess={() => {
            setShowCreateModal(false);
            fetchInvoices();
          }}
        />
      )}
    </div>
  );
}

// Create Invoice Modal Component
function CreateInvoiceModal({
  quotes,
  config,
  onClose,
  onSuccess,
}: {
  quotes: Quote[];
  config: {
    biz_name: string;
    biz_type: string;
    owner_name?: string;
    contact_email?: string;
    contact_phone?: string;
    abn?: string;
    bank_account_name?: string;
    bank_account_bsb?: string;
    bank_account_number?: string;
    gst_registered?: boolean;
  };
  onClose: () => void;
  onSuccess: () => void;
}) {
  const [selectedQuoteId, setSelectedQuoteId] = useState('');
  const [dueDate, setDueDate] = useState(toDateInputValue(new Date(Date.now() + 14 * 24 * 60 * 60 * 1000)));
  const [notes, setNotes] = useState('');
  const [creating, setCreating] = useState(false);
  const [manualItems, setManualItems] = useState<Array<{
    service_name: string;
    description: string;
    quantity: number;
    price: number;
  }>>([{ service_name: '', description: '', quantity: 1, price: 0 }]);

  const selectedQuote = quotes.find(q => q.id === selectedQuoteId);
  const hasQuote = selectedQuoteId !== '';

  const handleAddManualItem = () => {
    setManualItems([...manualItems, { service_name: '', description: '', quantity: 1, price: 0 }]);
  };

  const handleRemoveManualItem = (index: number) => {
    setManualItems(manualItems.filter((_, i) => i !== index));
  };

  const handleUpdateManualItem = (index: number, field: string, value: any) => {
    const updated = [...manualItems];
    updated[index] = { ...updated[index], [field]: value };
    setManualItems(updated);
  };

  const handleCreate = async () => {
    if (!dueDate) {
      toast.error('Please select a due date');
      return;
    }

    if (!hasQuote && manualItems.some(i => !i.service_name || i.price <= 0)) {
      toast.error('Please fill in all line item details');
      return;
    }

    setCreating(true);

    try {
      // Get current user
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) {
        toast.error('You must be logged in');
        setCreating(false);
        return;
      }

      // Generate invoice number
      const invoiceNumber = `INV-${Date.now().toString().slice(-6)}`;

      // Get customer data from quote or manual entry
      const customerName = hasQuote && selectedQuote ? selectedQuote.customer_name : '';
      const customerEmail = hasQuote && selectedQuote ? selectedQuote.customer_email : '';
      const customerPhone = hasQuote && selectedQuote ? selectedQuote.customer_phone : '';
      const customerAddress = hasQuote && selectedQuote ? selectedQuote.customer_address : '';

      // Calculate totals from line items
      let subtotal = 0;
      let lineItemsData = [];

      if (hasQuote && selectedQuote) {
        // Fetch quote items
        const { data: quoteItems, error: quoteItemsError } = await supabase
          .from('quote_items')
          .select('*')
          .eq('quote_id', selectedQuote.id);

        if (quoteItemsError) {
          console.error('Quote items fetch error:', quoteItemsError);
          throw new Error('Failed to fetch quote items: ' + quoteItemsError.message);
        }

        subtotal = quoteItems?.reduce((sum, item) => sum + (item.quantity * item.price), 0) || 0;
        lineItemsData = quoteItems?.map(item => ({
          service_name: item.service_name,
          description: item.description || '',
          quantity: item.quantity,
          price: item.price,
        })) || [];
      } else {
        // Use manual items
        subtotal = manualItems.reduce((sum, item) => sum + (item.quantity * item.price), 0);
        lineItemsData = manualItems.map(item => ({
          service_name: item.service_name,
          description: item.description,
          quantity: item.quantity,
          price: item.price,
        }));
      }

      // Calculate GST and total
      const gstRate = config?.gst_registered ? 0.1 : 0;
      const gstAmount = subtotal * gstRate;
      const totalAmount = subtotal + gstAmount;

      // Create invoice
      const { data: invoice, error: invoiceError } = await supabase
        .from('invoices')
        .insert([{
          user_id: user.id,
          invoice_number: invoiceNumber,
          quote_id: hasQuote && selectedQuote ? selectedQuote.id : null,
          customer_name: customerName,
          customer_email: customerEmail,
          customer_phone: customerPhone,
          customer_address: customerAddress,
          subtotal,
          gst_amount: gstAmount,
          total_amount: totalAmount,
          amount_paid: 0,
          amount_due: totalAmount,
          status: 'unpaid',
          due_date: new Date(dueDate).toISOString(),
          notes,
          email_sent: false,
        }])
        .select()
        .single();

      if (invoiceError) {
        console.error('Invoice creation error:', invoiceError);
        throw new Error('Failed to create invoice: ' + invoiceError.message);
      }


      // Create invoice items (TAX COMPLIANT - LINE ITEMS REQUIRED)
      if (lineItemsData.length > 0) {
        const items = lineItemsData.map(item => ({
          invoice_id: invoice.id,
          service_name: item.service_name,
          description: item.description,
          quantity: item.quantity,
          price: item.price,
          // Preserve FK references if they exist on the item
          service_id: (item as any).service_id || null,
          inventory_item_id: (item as any).inventory_item_id || null,
          quantity_required: (item as any).quantity_required || null,
        }));


        const { error: itemsError } = await supabase
          .from('invoice_items')
          .insert(items);

        if (itemsError) {
          console.error('Invoice items error:', itemsError);
          // Rollback: delete the invoice
          await supabase.from('invoices').delete().eq('id', invoice.id);
          throw new Error('Failed to create invoice items: ' + itemsError.message);
        }

        // Reserve stock for items with inventory_item_id
        const { warnings } = await reserveStockForInvoice(invoice.id, user.id);
        if (warnings.length > 0) {
          // Show warnings but don't block invoice creation
          warnings.forEach(w => toast.warning(w));
        }
      }

      // Update quote with invoice_id if applicable
      if (hasQuote && selectedQuote) {
        const { error: updateQuoteError } = await supabase
          .from('quotes')
          .update({ invoice_id: invoice.id })
          .eq('id', selectedQuote.id);

        if (updateQuoteError) {
          console.error('Quote update error:', updateQuoteError);
          // Don't fail, just warn
          toast.warning('Invoice created but failed to link to quote');
        }
      }

      toast.success('Invoice created successfully with line items!');

      // Ask to send email
      if (window.confirm('Invoice created! Would you like to send it now?')) {
        try {
          await fetch('/api/send-invoice', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({
              invoice: {
                ...invoice,
                line_items: lineItemsData,
              },
              config,
            }),
          });
          toast.success('Invoice sent to customer!');
          await supabase
            .from('invoices')
            .update({ email_sent: true, emailed_at: new Date().toISOString() })
            .eq('id', invoice.id);
        } catch (emailError) {
          console.error('Email error:', emailError);
          toast.warning('Invoice created but failed to send email');
        }
      }

      onSuccess();
    } catch (error) {
      console.error('Invoice creation error:', error);
      const errorMsg = error instanceof Error ? error.message : 'Unknown error occurred';
      toast.error('Failed to create invoice: ' + errorMsg);
    } finally {
      setCreating(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-3xl p-6 max-w-2xl w-full max-h-[90vh] overflow-y-auto">
        <h3 className="text-xl font-black text-slate-900 uppercase mb-4">Create Tax-Compliant Invoice</h3>

        <div className="space-y-4">
          {/* Quote Selection */}
          <div>
            <label className="block text-[10px] font-black text-slate-500 uppercase mb-1">
              Select Approved Quote (Optional)
            </label>
            <select
              value={selectedQuoteId}
              onChange={(e) => setSelectedQuoteId(e.target.value)}
              className="w-full h-12 rounded-xl border border-slate-200 px-4 font-bold text-slate-900 focus:border-purple-600 focus:outline-none"
            >
              <option value="">-- Manual Entry (Create Custom Invoice) --</option>
              {quotes.map(quote => (
                <option key={quote.id} value={quote.id}>
                  {quote.customer_name} - ${quote.total_amount.toFixed(2)}
                </option>
              ))}
            </select>
          </div>

          {/* Manual Line Items (shown when no quote selected) */}
          {!hasQuote && (
            <div className="bg-slate-50 p-4 rounded-xl space-y-3">
              <div className="flex justify-between items-center">
                <p className="text-sm font-black text-slate-700 uppercase">Invoice Line Items</p>
                <Button onClick={handleAddManualItem} size="sm" variant="outline">
                  <Plus className="w-3 h-3 mr-1" /> Add Item
                </Button>
              </div>
              {manualItems.map((item, index) => (
                <div key={index} className="grid grid-cols-12 gap-2 items-start">
                  <input
                    type="text"
                    placeholder="Service name *"
                    value={item.service_name}
                    onChange={(e) => handleUpdateManualItem(index, 'service_name', e.target.value)}
                    className="col-span-4 h-10 rounded-lg border border-slate-200 px-3 text-sm font-bold"
                  />
                  <input
                    type="text"
                    placeholder="Description"
                    value={item.description}
                    onChange={(e) => handleUpdateManualItem(index, 'description', e.target.value)}
                    className="col-span-4 h-10 rounded-lg border border-slate-200 px-3 text-sm"
                  />
                  <input
                    type="number"
                    placeholder="Qty"
                    value={item.quantity}
                    onChange={(e) => handleUpdateManualItem(index, 'quantity', parseFloat(e.target.value) || 1)}
                    className="col-span-1 h-10 rounded-lg border border-slate-200 px-3 text-sm text-center"
                  />
                  <input
                    type="number"
                    placeholder="Price $"
                    value={item.price}
                    onChange={(e) => handleUpdateManualItem(index, 'price', parseFloat(e.target.value) || 0)}
                    className="col-span-2 h-10 rounded-lg border border-slate-200 px-3 text-sm text-right"
                  />
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => handleRemoveManualItem(index)}
                    className="col-span-1 h-10 w-10 p-0 text-red-600"
                  >
                    <Trash2 className="w-4 h-4" />
                  </Button>
                </div>
              ))}
              <div className="flex justify-between items-center pt-2 border-t">
                <p className="text-xs font-bold text-slate-500">Add all services/products as separate line items</p>
                <p className="text-sm font-black text-purple-600">
                  Subtotal: ${manualItems.reduce((sum, item) => sum + (item.quantity * item.price), 0).toFixed(2)}
                </p>
              </div>
            </div>
          )}

          {/* Quote Preview */}
          {hasQuote && selectedQuote && (
            <div className="bg-slate-50 p-4 rounded-xl">
              <p className="text-sm font-bold text-slate-900">Customer: {selectedQuote.customer_name}</p>
              <p className="text-xs text-slate-600">Email: {selectedQuote.customer_email}</p>
              <p className="text-lg font-black text-purple-600 mt-2">
                Total: ${selectedQuote.total_amount.toFixed(2)}
              </p>
              <p className="text-xs text-slate-500 mt-1">Line items will be copied from quote</p>
            </div>
          )}

          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-[10px] font-black text-slate-500 uppercase mb-1">
                Due Date *
              </label>
              <input
                type="date"
                value={dueDate}
                onChange={(e) => setDueDate(e.target.value)}
                className="w-full h-12 rounded-xl border border-slate-200 px-4 font-bold text-slate-900 focus:border-purple-600 focus:outline-none"
              />
              <p className="text-[9px] text-slate-500 mt-1">Format: DD/MM/YYYY</p>
            </div>

            <div>
              <label className="block text-[10px] font-black text-slate-500 uppercase mb-1">
                Notes (Optional)
              </label>
              <Input
                value={notes}
                onChange={(e) => setNotes(e.target.value)}
                placeholder="Payment terms, etc."
                className="h-12 rounded-xl"
              />
            </div>
          </div>

          <div className="flex gap-2 pt-4">
            <Button variant="outline" onClick={onClose} className="flex-1">
              Cancel
            </Button>
            <Button
              onClick={handleCreate}
              disabled={creating || (!hasQuote && manualItems.length === 0) || !dueDate}
              className="flex-1 bg-purple-600 hover:bg-purple-700"
            >
              {creating ? 'Creating...' : 'Create Invoice'}
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
}
